import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public live-assessment view — phone-first, no auth, polled every 3s.
 *
 * Parent scans a QR shown on the coach's screen → lands here. They see
 * their kids' test results animate in real-time as the coach enters them.
 * Pricing reveals at the 20-min mark (or whenever the coach manually flips
 * the flag) so the parent stays focused on results until close time.
 *
 * Tab per kid for multi-athlete sessions (per spec, MULTIPLE kids on one
 * parent each get their own tab).
 */
export default function LiveAssessment() {
    const { liveViewId } = useParams();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        let timer = null;
        const tick = async () => {
            try {
                const r = await axios.get(`${API}/live/${liveViewId}`);
                if (alive) {
                    setData(r.data);
                    setError(null);
                    setBusy(false);
                }
            } catch (e) {
                if (alive) {
                    setError(e?.response?.status === 404 ? "not_found" : "network");
                    setBusy(false);
                }
            }
            if (alive) timer = setTimeout(tick, 3000);
        };
        tick();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [liveViewId]);

    if (busy) return <FullScreenSpinner />;

    if (error === "not_found") {
        return (
            <FullScreenMsg
                title="Link not active"
                msg="This live link isn't active or has expired. If you're at the gym, please grab the attention of one of our coaches and we'll get you the right link."
            />
        );
    }
    if (error === "network") {
        return (
            <FullScreenMsg
                title="Lost connection"
                msg="Trying to reconnect..."
                spinner
            />
        );
    }

    if (data?.expired) {
        return (
            <FullScreenMsg
                title="Assessment complete"
                msg="The full recap was emailed to you — check your inbox. You can also revisit it any time from your dashboard."
            />
        );
    }

    const athletes = data?.athletes || [];
    if (athletes.length === 0) {
        return (
            <FullScreenMsg
                title="Setting up your view"
                msg="Hang tight — we're getting things ready."
                spinner
            />
        );
    }
    const active = athletes[Math.min(activeIdx, athletes.length - 1)];

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-md mx-auto px-4 py-4">
                <Header coachName={data.coach_name} />
                {athletes.length > 1 && (
                    <AthleteTabs
                        athletes={athletes}
                        activeIdx={activeIdx}
                        onPick={setActiveIdx}
                    />
                )}
                <AthletePanel athlete={active} key={active.athlete_id} />
                <PricingPanel data={data} />
                <Footer />
            </div>
        </div>
    );
}

function Header({ coachName }) {
    return (
        <div className="flex items-center gap-2 mb-5 mt-2">
            <span className="inline-flex items-center gap-1.5 bg-brand-red/10 border border-brand-red/40 px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
                <span className="text-[10px] tracking-[0.22em] uppercase text-brand-red font-semibold">LIVE</span>
            </span>
            <div className="text-[11px] tracking-[0.15em] uppercase text-zinc-400">
                with {coachName}
            </div>
        </div>
    );
}

function AthleteTabs({ athletes, activeIdx, onPick }) {
    return (
        <div className="flex gap-1 mb-4 border-b border-zinc-900" data-testid="live-athlete-tabs">
            {athletes.map((a, i) => (
                <button
                    type="button"
                    key={a.athlete_id || a.name}
                    onClick={() => onPick(i)}
                    data-testid={`live-tab-${i}`}
                    className={`px-3 py-2 text-xs tracking-[0.12em] uppercase border-b-2 transition-colors ${
                        i === activeIdx
                            ? "border-brand-red text-white"
                            : "border-transparent text-zinc-500 hover:text-zinc-200"
                    }`}
                >
                    {a.name}
                </button>
            ))}
        </div>
    );
}

function AthletePanel({ athlete }) {
    const tests = athlete.tests || [];
    const filledCount = tests.filter((t) => t.filled).length;
    const totalEnabled = tests.filter((t) => t.enabled || t.filled).length;
    return (
        <div className="border border-zinc-900 bg-zinc-950 p-4 mb-4" data-testid="live-athlete-panel">
            <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-2xl tracking-wider text-white">{athlete.name}</h2>
                {totalEnabled > 0 && (
                    <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                        {filledCount} / {totalEnabled}
                    </div>
                )}
            </div>
            <TestList tests={tests} />
            {athlete.extra_notes && (
                <div className="mt-4 border-l-2 border-brand-red pl-3 text-sm text-zinc-200 leading-relaxed">
                    {athlete.extra_notes}
                </div>
            )}
        </div>
    );
}

function TestList({ tests }) {
    // Show every test as a row. Filled = white value. Not yet filled but
    // enabled = "pending..." gray placeholder. Not enabled = skip entirely.
    const visible = useMemo(
        () => tests.filter((t) => t.enabled || t.filled),
        [tests],
    );
    if (visible.length === 0) {
        return (
            <div className="text-center text-xs text-zinc-500 py-8 italic">
                Coach is setting up — results will appear here as we go.
            </div>
        );
    }
    return (
        <div className="space-y-2">
            {visible.map((t) => (
                <TestRow key={t.key} test={t} />
            ))}
        </div>
    );
}

function TestRow({ test }) {
    return (
        <div
            data-testid={`live-test-${test.key}`}
            className={`flex items-baseline justify-between gap-3 py-2 border-b border-zinc-900 last:border-b-0 transition-all ${
                test.filled ? "" : "opacity-50"
            }`}
        >
            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400 font-mono shrink-0">
                {test.label}
            </div>
            {test.filled ? (
                <div className="flex items-center gap-1.5 text-sm text-white font-mono text-right">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                    {test.value}
                </div>
            ) : (
                <div className="text-xs text-zinc-600 italic font-mono text-right">pending…</div>
            )}
        </div>
    );
}

function PricingPanel({ data }) {
    const revealed = data.pricing_revealed;
    const secs = data.seconds_since_start || 0;
    const target = data.pricing_reveal_after_seconds || 1200;
    const pct = Math.min(100, Math.round((secs / target) * 100));
    return (
        <div
            data-testid="live-pricing-panel"
            className="border border-zinc-900 bg-zinc-950 p-4 mb-4"
        >
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">
                Recommendation
            </div>
            {revealed ? (
                <PricingRevealed />
            ) : (
                <PricingPending pct={pct} />
            )}
        </div>
    );
}

function PricingPending({ pct }) {
    return (
        <div className="text-center py-4">
            <Clock className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
            <div className="text-sm text-zinc-200 mb-3 leading-relaxed">
                Your recommendation is being prepared.
                <br />
                <span className="text-zinc-500 text-xs">Coach will share when we're done.</span>
            </div>
            <div className="h-1 bg-zinc-900 overflow-hidden">
                <div
                    className="h-full bg-brand-red/50 transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function PricingRevealed() {
    return (
        <div className="text-center py-2">
            <div className="text-sm text-white leading-relaxed">
                Coach will walk you through your recommended path now.
            </div>
            <div className="text-xs text-zinc-500 mt-1">
                We'll also email the full breakdown.
            </div>
        </div>
    );
}

function Footer() {
    return (
        <div className="text-center text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-6 pb-6">
            Maximum Sports Conditioning
        </div>
    );
}

function FullScreenSpinner() {
    return (
        <div className="min-h-screen bg-black flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
    );
}

function FullScreenMsg({ title, msg, spinner }) {
    return (
        <div className="min-h-screen bg-black flex items-center justify-center px-6">
            <div className="text-center max-w-sm">
                <div className="text-[11px] tracking-[0.25em] uppercase text-brand-red mb-3">
                    Maximum Sports Conditioning
                </div>
                <h1 className="font-display text-2xl tracking-wider text-white mb-3">{title}</h1>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">{msg}</p>
                {spinner && <Loader2 className="w-5 h-5 text-zinc-500 animate-spin mx-auto" />}
            </div>
        </div>
    );
}
